"use client";

import { cn } from "@/lib/utils";
import { KICKOFF_TIME, LIVE_START, kickoffParts } from "./time";
import type { CountNoun } from "./subject";
import { StarIcon } from "./star-button";

const KICKOFF_VARIANTS = {
  row: {
    frame: "flex h-[34px] items-center gap-[10px] border-t border-b border-t-white/[0.12] border-b-surface-line bg-surface-overlay px-4",
    date: "truncate text-[12px] font-semibold text-foreground",
  },
  card: {
    frame: "flex items-baseline gap-2 px-3 pb-2 pt-3.5 text-[12px] font-semibold",
    date: "text-foreground",
  },
} as const;

export function KickoffHeader({ startMs, showDate, starred = false, variant = "row" }: { startMs: number | null; showDate: boolean; starred?: boolean; variant?: keyof typeof KICKOFF_VARIANTS }) {
  const { frame, date } = KICKOFF_VARIANTS[variant];
  const star = starred && <span className={cn(date, "flex items-center gap-1.5 self-center")}><StarIcon on size={12} className="text-primary" />Starred</span>;
  if (startMs === LIVE_START) return <div className={frame}>{star}<span className={date}>Live</span></div>;
  const parts = startMs ? kickoffParts(startMs) : null;
  return (
    <div className={frame}>
      {star}
      {(parts === null || showDate) && <span className={date}>{parts ? parts.date : "Scheduled"}</span>}
      {parts && <span className={KICKOFF_TIME}>{parts.time}</span>}
    </div>
  );
}

const EVENT_FRAMES = {
  strip: "flex w-full items-center gap-2 border-t border-b border-t-white/[0.12] border-b-surface-line bg-surface-overlay text-left hover:bg-white/[0.06]",
  card: "flex min-h-11 w-full items-center gap-2 border border-white/[0.06] bg-surface-overlay px-3 text-left active:bg-white/[0.06]",
} as const;

export function EventGroupHeader({ label, date = null, time, count, noun, collapsed, compact = false, variant = "strip", onToggle }: {
  label: string;
  date?: string | null;
  time: string;
  count: number;
  noun: CountNoun;
  collapsed: boolean;
  compact?: boolean;
  variant?: keyof typeof EVENT_FRAMES;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-expanded={!collapsed}
      onClick={onToggle}
      className={cn(EVENT_FRAMES[variant], variant === "card" ? (collapsed ? "rounded-[10px]" : "rounded-t-[10px]") : compact ? "h-11 px-3" : "h-9 px-4")}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={cn("text-zinc-500 transition-transform", collapsed && "-rotate-90")}><path d="m6 9 6 6 6-6" /></svg>
      <span className="min-w-0 truncate text-[12px] font-semibold text-foreground">{label}</span>
      {date !== null && <span className="flex-shrink-0 whitespace-nowrap text-[12px] text-zinc-400">{date}</span>}
      <span className={cn(KICKOFF_TIME, "flex-shrink-0")}>{time}</span>
      {collapsed && <span className="data-num ml-auto flex-shrink-0 whitespace-nowrap text-[11px] text-zinc-500">{count} {count === 1 ? noun.one : noun.many}</span>}
    </button>
  );
}
